import type { Lang } from '@account-manager/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api.js';
import { useAuth } from '../../auth/AuthProvider.js';
import type { ConfigStatus } from '../../types/onboarding.js';

interface EpRow {
  id: string;
  label: string;
  establishment_code: string;
  pos_code: string;
}

interface EpPref {
  emission_point_id: string;
}

export function Profile() {
  const { t, i18n } = useTranslation(['settings', 'auth', 'bookkeeping']);
  const { user, activeOrgId } = useAuth();
  const qc = useQueryClient();
  const [name, setName] = useState(user?.name ?? '');
  const [lang, setLang] = useState<Lang>((i18n.language as Lang) ?? 'es');
  const [saved, setSaved] = useState(false);

  // Org SRI config (Phase 5b)
  const { data: status } = useQuery({
    queryKey: ['config-status', activeOrgId],
    queryFn: () => api<ConfigStatus>(`/orgs/${activeOrgId}/config-status`),
    enabled: !!activeOrgId,
  });
  const [establishment, setEstablishment] = useState('001');
  const [pos, setPos] = useState('001');
  const [sriEnvironment, setSriEnvironment] = useState<'1' | '2'>('1');
  const [orgSaved, setOrgSaved] = useState(false);
  const [orgError, setOrgError] = useState<string | null>(null);

  // Emission point preference
  const { data: epList } = useQuery({
    queryKey: ['emission-points', activeOrgId],
    queryFn: () => api<EpRow[]>('/bookkeeping/emission-points'),
    enabled: !!activeOrgId,
  });
  const { data: epPref } = useQuery({
    queryKey: ['emission-point-preference', activeOrgId],
    queryFn: () => api<EpPref | null>('/bookkeeping/me/emission-point-preference'),
    enabled: !!activeOrgId,
  });
  const setPrefMut = useMutation({
    mutationFn: (id: string) =>
      api('/bookkeeping/me/emission-point-preference', {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ emission_point_id: id }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['emission-point-preference'] }),
  });

  useEffect(() => {
    if (status?.organization) {
      setEstablishment(status.organization.establishment ?? '001');
      setPos(status.organization.pos ?? '001');
      setSriEnvironment(status.organization.sriEnvironment ?? '1');
    }
  }, [status?.organization]);

  const save = async () => {
    await api('/users/me', {
      method: 'PATCH',
      body: JSON.stringify({ name, preferredLang: lang }),
    });
    void i18n.changeLanguage(lang);
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  };

  const saveOrgSri = async () => {
    setOrgError(null);
    try {
      await api(`/orgs/${activeOrgId}/config`, {
        method: 'PATCH',
        body: JSON.stringify({ establishment, pos, sriEnvironment }),
      });
      await qc.invalidateQueries({ queryKey: ['config-status'] });
      setOrgSaved(true);
      setTimeout(() => setOrgSaved(false), 1500);
    } catch (e: unknown) {
      const err = e as { body?: { error?: string }; message?: string };
      setOrgError(err.body?.error ?? err.message ?? 'Error');
    }
  };

  return (
    <div className="max-w-md space-y-6">
      <div>
        <h1 className="mb-4 text-xl font-semibold">{t('settings:profile')}</h1>
        <div className="space-y-3">
          <label className="block">
            <span className="text-sm">{t('auth:name')}</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full rounded border px-3 py-2"
            />
          </label>
          <label className="block">
            <span className="text-sm">{t('settings:preferredLanguage')}</span>
            <select
              value={lang}
              onChange={(e) => setLang(e.target.value as Lang)}
              className="mt-1 w-full rounded border px-3 py-2"
            >
              <option value="es">Español</option>
              <option value="en">English</option>
            </select>
          </label>
          <button
            type="button"
            onClick={() => void save()}
            className="rounded bg-blue-600 px-4 py-2 text-white"
          >
            {t('settings:save', { defaultValue: 'Guardar' })}
          </button>
          {saved && <span className="ml-3 text-sm text-green-600">✓</span>}
        </div>
      </div>

      <div>
        <h2 className="mb-2 text-lg font-semibold">{t('settings:sriConfig')}</h2>
        <p className="mb-3 text-xs text-slate-600">{t('settings:sriConfigHint')}</p>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm">{t('settings:establishment')}</span>
              <input
                value={establishment}
                onChange={(e) => setEstablishment(e.target.value)}
                pattern="\d{3}"
                maxLength={3}
                className="mt-1 w-full rounded border px-3 py-2 font-mono"
              />
            </label>
            <label className="block">
              <span className="text-sm">{t('settings:pos')}</span>
              <input
                value={pos}
                onChange={(e) => setPos(e.target.value)}
                pattern="\d{3}"
                maxLength={3}
                className="mt-1 w-full rounded border px-3 py-2 font-mono"
              />
            </label>
          </div>
          <div>
            <span className="text-sm">{t('settings:sriEnvironment')}</span>
            <div className="mt-1 flex gap-4 text-sm">
              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  value="1"
                  checked={sriEnvironment === '1'}
                  onChange={() => setSriEnvironment('1')}
                />
                <span>{t('settings:sriPruebas')}</span>
              </label>
              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  value="2"
                  checked={sriEnvironment === '2'}
                  onChange={() => setSriEnvironment('2')}
                />
                <span>{t('settings:sriProduccion')}</span>
              </label>
            </div>
          </div>
          <button
            type="button"
            onClick={() => void saveOrgSri()}
            className="rounded bg-blue-600 px-4 py-2 text-white"
          >
            {t('settings:save', { defaultValue: 'Guardar' })}
          </button>
          {orgSaved && <span className="ml-3 text-sm text-green-600">✓</span>}
          {orgError && <span className="ml-3 text-sm text-red-600">{orgError}</span>}
        </div>
      </div>

      {epList && epList.length >= 2 && (
        <div className="rounded border bg-white p-4">
          <h2 className="mb-2 text-sm font-semibold">
            {t('bookkeeping:profile.default_emission_point')}
          </h2>
          <select
            value={epPref?.emission_point_id ?? ''}
            onChange={(e) => setPrefMut.mutate(e.target.value)}
            className="block w-full rounded border px-2 py-1 text-sm"
          >
            {epList.map((ep) => (
              <option key={ep.id} value={ep.id}>
                {ep.label} ({ep.establishment_code}-{ep.pos_code})
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}
